/**
 * The iPhone and Android apps (D-126) are a Capacitor shell that loads this same site. Everything here is a no-op in a
 * browser; in the app it hides the splash once the page has drawn, keeps the status bar dark, and turns our vibration
 * calls into real haptics (iPhone Safari has no navigator.vibrate, so the app is the only place iPhones feel them).
 * The plugin modules are thin wrappers (their web code loads only if called), so importing them costs the site little.
 */
import { Capacitor } from "@capacitor/core";
import { Haptics, ImpactStyle } from "@capacitor/haptics";
import { SplashScreen } from "@capacitor/splash-screen";
import { StatusBar, Style } from "@capacitor/status-bar";
import { watchTextScale } from "./textScale";

export const isNativeApp = (): boolean => Capacitor.isNativePlatform();
export const nativePlatform = (): "ios" | "android" | "web" => Capacitor.getPlatform() as "ios" | "android" | "web";

export async function startNative(): Promise<void> {
  if (!isNativeApp()) return;
  document.documentElement.dataset.native = nativePlatform();
  // A scrolled page gets a strip behind the status bar (styles/base.css), so text doesn't run under the clock (D-127).
  const root = document.documentElement;
  const onScroll = () => { if ((window.scrollY > 0) !== ("scrolled" in root.dataset)) root.toggleAttribute("data-scrolled", window.scrollY > 0); };
  window.addEventListener("scroll", onScroll, { passive: true });
  watchTextScale();
  try { await StatusBar.setStyle({ style: Style.Dark }); } catch { /* not fatal */ }
  // Two frames: the first paint of the card room, not the empty root.
  requestAnimationFrame(() => requestAnimationFrame(() => { void SplashScreen.hide({ fadeOutDuration: 200 }); }));
}

/** A vibration length in ms (as passed to navigator.vibrate) mapped to the nearest native impact. */
export function nativeHaptic(ms: number): void {
  Haptics.impact({ style: ms >= 25 ? ImpactStyle.Heavy : ms >= 12 ? ImpactStyle.Medium : ImpactStyle.Light }).catch(() => { /* unsupported */ });
}
